// src/views/Users/Customers/Modal/AddProjectModal.jsx
import React, { useState, useEffect } from "react";
import { Modal, Button } from "react-bootstrap";
import { useForm, Controller } from "react-hook-form";
import { modeConstants, recruiterConstants } from "../../constants";
import { recruiterActions } from "../../redux/actions";
import { useDispatch, useSelector } from "react-redux";
import PhoneInput from "react-phone-input-2";

const DEFAULT_VALUES = {
  recruiterName: "",
  recruiterEmail: "",
  country: "",
  state: "",
  city: "",
  contactNumber: "",
  gender: "",
  designation: "",
  location: "",
  details: "",
  userType: false,
};

const genderOptions = ["Male", "Female", "Other"];
const designationOptions = [
  "Senior Recruiter",
  "Talent Acquisition",
  "Lead Recruiter",
  "HR Generalist",
  "HR Manager",
];

export default function AddRecruiterModal({ show, onHide, mode }) {
  const isAdd = mode === modeConstants.ADD_MODE;
  const isEdit = mode === modeConstants.UPDATE_MODE;
  const isView = mode === modeConstants.VIEW_MODE;
  const [countryCode, setCountryCode] = useState("pk");
  const dispatch = useDispatch();
  const initialData = useSelector((s) => s.recruiterReducer.formData);

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors },
  } = useForm({
    defaultValues: DEFAULT_VALUES, mode: "onChange",          // validate on typing
    reValidateMode: "onChange",
    criteriaMode: "all",
  });

  // Prefill on open
  useEffect(() => {
    if (!show) return;

    if (isAdd) {
      reset(DEFAULT_VALUES);
      return;
    }

    if (initialData && (isEdit || isView)) {
      reset({
        recruiterName: initialData.name ?? "",
        recruiterEmail: initialData.email ?? "",
        country: initialData.country ?? "",
        state: initialData.state ?? "",
        city: initialData.city ?? "",
        contactNumber: String(initialData.contactNumber) ?? "",
        gender: initialData.gender ?? "",
        nationality: initialData.nationality ?? "",
        designation: initialData.designation ?? "",
        location: initialData.location ?? "",
        details: initialData.details ?? "",
        userType:
          initialData.userTypeId == recruiterConstants.MANAGER ? true : false,
      });
      return;
    }

    reset(DEFAULT_VALUES);
  }, [show, isAdd, isEdit, isView, initialData, reset]);

  const onSubmit = (values) => {
    const payload = {
      id: initialData.id ?? 0,
      name: values.recruiterName || "",
      email: values.recruiterEmail || "",
      userTypeId: values.userType
        ? recruiterConstants.MANAGER
        : recruiterConstants.RECRUITER,
      country: values.country || "",
      state: values.state || "",
      city: values.city || "",
      contactNumber: values.contactNumber || "",
      gender: values.gender || "",
      nationality: values.nationality || "",
      designation: values.designation || "",
      location: values.location || "",
      details: values.details || "",
    };

    if (isEdit) {
      dispatch(recruiterActions.updateRecruiter(payload, onHide));
    } else {
      dispatch(recruiterActions.createRecruiter(payload, onHide));
    }
  };
  const title = isAdd
    ? "Add Recruiter"
    : isEdit
      ? "Edit Recruiter"
      : "View Recruiter";

  return (
    <Modal show={show} onHide={onHide} size="lg" backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <form onSubmit={handleSubmit(onSubmit)}>
          <fieldset disabled={isView}>
            <div className="row g-3">
              {/* Row 1: Recruiter Name | Recruiter Email */}
              <div className="col-12 col-lg-6">
                <div class="form-floating">
                  <input
                    id="name"
                    className="form-control"
                    placeholder="Recruiter Name"
                    {...register("recruiterName", {
                      required: "The field is required",
                      maxLength: {
    value: 50,
    message: "Maximum 50 characters allowed",
  },
                    })}
                  />
                  <label for="name">Recruiter Name</label>
                </div>
                {errors.recruiterName && (
                  <small className="text-danger">
                    {errors.recruiterName.message}
                  </small>
                )}
              </div>
              <div className="col-12 col-lg-6">
                <div class="form-floating">
                  <input
                    id="email"
                    type="email"
                    className="form-control"
                    placeholder="Recruiter Email"
                    {...register("recruiterEmail", {
                      required: "The field is required",
                      maxLength: {
    value: 100,
    message: "Maximum 100 characters allowed",
  },
                      pattern: {
                        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                        message: "Invalid email",
                      },
                    })}
                    disabled={isEdit || isView}
                  />
                  <label for="email">Email</label>
                </div>
                {errors.recruiterEmail && (
                  <small className="text-danger">
                    {errors.recruiterEmail.message}
                  </small>
                )}
              </div>

              {/* Row 2: Country | State */}
              <div className="col-12 col-lg-6">
                <div class="form-floating">
                  <input
                    id="country"
                    className="form-control"
                    placeholder="Country"
                    {...register("country", {
                      required: "The field is required",
                      maxLength: {
    value: 100,
    message: "Maximum 100 characters allowed",
  },
                    })}
                  />
                  <label for="country">Country</label>
                </div>
                {errors.country && (
                  <small className="text-danger">
                    {errors.country.message}
                  </small>
                )}
              </div>
              <div className="col-12 col-lg-6">
                <div class="form-floating">
                  <input
                    id="state"
                    className="form-control"
                    placeholder="State"
                    {...register("state", {
                      required: "The field is required",
                      maxLength: {
    value: 100,
    message: "Maximum 100 characters allowed",
  },
                    })}
                  />
                  <label for="state">State</label>
                </div>
                {errors.state && (
                  <small className="text-danger">{errors.state.message}</small>
                )}
              </div>

              {/* Row 3: City | 92 + Contact Number */}
              <div className="col-12 col-lg-6">
                <div class="form-floating">
                  <input
                    id="city"
                    className="form-control"
                    placeholder="City"
                    {...register("city", {
                      required: "The field is required",
                      maxLength: {
    value: 100,
    message: "Maximum 100 characters allowed",
  },
                    })}
                  />
                  <label for="city">
                    City
                  </label>
                </div>
                {errors.city && (
                  <small className="text-danger">{errors.city.message}</small>
                )}
              </div>
              <div className="col-12 col-lg-6">
                <Controller
                  name="contactNumber"
                  control={control}
                  rules={{
                    required: "The field is required",
                    validate: (val) =>
                      (val && val.length >= 8) || "Invalid number",
                  }}
                  render={({ field: { value, onChange } }) => (
                    <PhoneInput
                      country={countryCode}
                      value={value.toString()}
                      onChange={(val, country) => {
                        onChange(val);
                        if (country?.countryCode)
                          setCountryCode(country.countryCode);
                      }}
                      inputProps={{
                        name: "contactNumber",
                        required: true,
                        autoFocus: false,
                      }}
                      placeholder="Enter phone number"
                      enableSearch
                      disableSearchIcon={false}
                      inputStyle={{ width: "100%" }}
                      buttonStyle={{
                        borderTopLeftRadius: 6,
                        borderBottomLeftRadius: 6,
                      }}
                      containerStyle={{ width: "100%" }}
                      disabled={isView}
                    />
                  )}
                />
                {errors.contactNumber && (
                  <small className="text-danger">
                    {errors.contactNumber.message}
                  </small>
                )}
              </div>

              {/* Row 4: Gender | Select Designation */}
              <div className="col-12 col-lg-6">
                <div class="form-floating">
                  <select
                    id="gender"
                    className="form-control"
                    {...register("gender", {
                      required: "The field is required",
                    })}
                  >
                    <option value="">Gender</option>
                    {genderOptions.map((g) => (
                      <option key={g} value={g}>
                        {g}
                      </option>
                    ))}
                  </select>
                  <label for="gender">
                    Gender
                  </label>
                </div>
                {errors.gender && (
                  <small className="text-danger">{errors.gender.message}</small>
                )}
              </div>
              <div className="col-12 col-lg-6">
                <div class="form-floating">
                  <select
                    id="designation"
                    className="form-control"
                    {...register("designation", {
                      required: "The field is required",
                    })}
                  >
                    <option value="">Select Designation</option>
                    {designationOptions.map((d) => (
                      <option key={d} value={d}>
                        {d}
                      </option>
                    ))}
                  </select>
                  <label for="designation">
                    Designation
                  </label>
                </div>
                {errors.designation && (
                  <small className="text-danger">
                    {errors.designation.message}
                  </small>
                )}
              </div>

              {/* Row 5: Location (full width) */}
              <div className="col-12">
                <div class="form-floating">
                  <input
                    id="location"
                    className="form-control"
                    placeholder="Location"
                    {...register("location", {
                      required: "The field is required",
                    })}
                  />
                  <label for="location">
                    Location
                  </label>
                </div>
                {errors.location && (
                  <small className="text-danger">
                    {errors.location.message}
                  </small>
                )}
              </div>

              {/* Row 6: details (full width) */}
              <div className="col-12">
                <div class="form-floating">
                  <textarea
                    id="detail"
                    className="form-control"
                    rows={6}
                    placeholder="Details"
                    {...register("details")}
                  />
                  <label for="detail">
                    Details
                  </label>
                </div>
              </div>
              {/* Row 7: details (full width) */}
              <div className="col-12 d-flex algin-items-center gap-2">
                <input
                  type="checkbox"
                  // className="form-control"
                  placeholder=""
                  {...register("userType")}
                />
                <label className="m-0">Is Manager</label>
              </div>
            </div>
          </fieldset>

          <div className="d-flex justify-content-end gap-2 mt-5 footer-modal">
            <Button variant="outline-secondary" onClick={onHide} className="btn-modal">
              {isView ? "Close" : "Cancel"}
            </Button>
            {!isView && (
              <Button type="submit" variant="success" className="btn-modal">
                {isEdit ? "Save Changes" : "Create Recruiter"}
              </Button>
            )}
          </div>
        </form>
      </Modal.Body>
    </Modal>
  );
}
